// The agent list inside the transfer form — click a colleague instead of
// typing their extension.
//
// WHY: the transfer form shipped as a bare "Extension or number…" input, which
// assumes the agent KNOWS the numbering plan. The agent testing it did not —
// nobody on a real floor memorises extensions either — and asked, reasonably,
// why the form doesn't just show who is on shift. So it does: every extension
// on the tenant, named, with its live presence, one click to choose.
//
// The states are shown honestly rather than filtered: a Ready colleague is the
// normal target; one on a call or paused is pickable but says so (their desk
// still rings on a direct consult — pause only blocks QUEUE calls); an offline
// one is disabled because there is no phone behind it and the consult would
// just ring into nothing. The free-text input stays for external numbers.
import { useEffect, useState } from "react";
import { opsFetch } from "../../api";
import { HxButton } from "../../components/hx";

type Row = {
  ext: string;
  name?: string | null;
  user_name?: string | null;
  presence?: string | null;
  active?: boolean;
};

// Presence colours are chosen from data, so they stay, on tokens: on a call is
// blue (never --violet); wrapping up and paused share the amber.
const DOT: Record<string, string> = {
  ready: "var(--ok)", oncall: "var(--blue)", wrap: "var(--warn)",
  paused: "var(--warn)", offline: "var(--muted-foreground)",
};
const LABEL: Record<string, string> = {
  ready: "ready", oncall: "on a call", wrap: "wrapping up",
  paused: "paused", offline: "offline",
};

export default function AgentPick({ excludeExt, onPick }: {
  /** The agent's own extension — you cannot consult yourself. */
  excludeExt?: string | null;
  onPick: (ext: string) => void;
}) {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let stop = false;
    void opsFetch<{ agents?: Row[] }>("/ops/api/voice/softphone/presence")
      .then((d) => { if (!stop) setRows((d.agents ?? []).filter((a) => a.active !== false)); })
      .catch(() => { if (!stop) setError(true); });
    return () => { stop = true; };
  }, []);

  // Failing to load the roster must not block the transfer — the typed-number
  // path is still there. Say nothing rather than show a broken list.
  if (error) return null;
  if (rows === null) {
    return <span className="w-full text-xs text-muted-foreground">Loading agents…</span>;
  }

  const list = rows.filter((a) => String(a.ext) !== String(excludeExt ?? ""));
  if (list.length === 0) return null;

  // The template's small outline button (pass 214). These were solid slate
  // literals once, because theme tokens resolved dark-on-dark in the call bar;
  // since pass 003 the tokens sit on :root and since pass 075 the bar is
  // bg-card, so the button's own colours read in both themes.
  return (
    <div className="flex w-full flex-wrap gap-1.5">
      {list.map((a) => {
        const p = a.presence ?? "offline";
        const offline = p === "offline";
        const who = a.user_name || a.name || `Ext ${a.ext}`;
        return (
          <HxButton key={a.ext} size="sm" disabled={offline}
            onClick={() => onPick(String(a.ext))}
            title={offline
              ? `${who} has no phone registered — a consult would ring into nothing`
              : `Consult ${who}`}>
            {/* Name and state only — the extension is plumbing the agent never
                needs to see; it still travels in onPick underneath. */}
            <span aria-hidden="true" className="size-2 flex-none rounded-full" style={{ background: DOT[p] ?? DOT.offline }} />
            {who}
            <span className="text-xs font-normal text-muted-foreground">{LABEL[p] ?? p}</span>
          </HxButton>
        );
      })}
    </div>
  );
}
